// src/hooks/useLocutorBackend.js
// Scheduler de locução com backend persistente + sincronização real com música

import { useState, useEffect, useCallback, useRef } from 'react';
import { addWebSocketListener, removeWebSocketListener, sendWebSocketMessage } from '../services/websocketSingleton';
import { apiFetch } from '../apiFetch';

const API = '';
const DURACAO_ESTIMADA    = 11; // segundos estimados com speakingRate 1.25

function getClienteId() {
  try { return JSON.parse(localStorage.getItem('radio-ai-user') || '{}').id || 'default'; }
  catch { return 'default'; }
}
function getIsIndoor() {
  try { return JSON.parse(localStorage.getItem('radio-ai-user') || '{}').tipoProduto === 'indoor'; }
  catch { return false; }
}

export function useLocutorBackend() {
  const clienteId = getClienteId();

  const [estado,         setEstado]         = useState({ ativo: false });
  const [falando,        setFalando]        = useState(false);
  const [ultimaFala,     setUltimaFala]     = useState('');
  const [status,         setStatus]         = useState('');
  const [segundosProx,   setSegundosProx]   = useState(null);
  const [textoImediato,  setTextoImediato]  = useState('');
  const [erroTextoImediato, setErroTextoImediato] = useState('');
  const pollRef   = useRef(null);

  // ─── Poll status do backend ────────────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/locutor-scheduler/status/${clienteId}`);
      if (!r.ok) return;
      const d = await r.json();
      setEstado(d);
      setSegundosProx(d.segundosParaProxima ?? null);
      setUltimaFala(d.ultimaFala || '');
      if (d.ativo) {
        const current = Number.isFinite(Number(d.faixasDesdeUltimaLocucao))
          ? Number(d.faixasDesdeUltimaLocucao)
          : 0;
        const target = Number.isFinite(Number(d.faixasPorLocucao))
          ? Number(d.faixasPorLocucao)
          : null;
        setStatus(target
          ? `🎵 ${current}/${target} faixas desde a última locução`
          : '✅ Locuções automáticas ativas — aguardando a próxima faixa');
      } else {
        setStatus('');
      }
    } catch {}
  }, [clienteId]);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 3000);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  // ─── WebSocket — ouve eventos do backend ───────────────────────────────────
  useEffect(() => {
    const handler = async (event) => {
      try {
        const msg = JSON.parse(event.data);

        // Backend gerou o áudio — agenda reprodução no frontend
        if (msg.type === 'LOCUTOR_AGENDADO' && msg.clienteId === clienteId) {
          if(msg.displayOnly===true){setStatus('Áudio preparado para o player da loja');fetchStatus();return;}
          if (getIsIndoor()) return; // indoor usa PLAY_LOCUTOR no exe, ignora aqui
          const audioUrl = msg.audioUrl.startsWith('http')
            ? msg.audioUrl
            : `${window.location.origin}${msg.audioUrl}`;
          window.dispatchEvent(new CustomEvent('locutor-audio-pronto', {
            detail: {
              audioUrl,
              duration:  msg.duration  || DURACAO_ESTIMADA,
              pauseMap:  msg.pauseMap  || null,
              bedUrl:    msg.bedUrl    || null,
              executeAt: msg.executeAt || null,
            }
          }));
          setUltimaFala('');
          setStatus('✅ Áudio agendado pelo backend');
          fetchStatus();
        }

        // Scheduler ativou — retoma playback
        if (msg.type === 'LOCUTOR_SCHEDULER_ATIVADO' && msg.clienteId === clienteId) {
          if (getIsIndoor()) {
            sendWebSocketMessage('PLAYER_RESUME');
          } else {
            const audio = window.audioCoordinator?.audioElement;
            if (audio && audio.paused) {
              audio.play().catch(() => {
                window.dispatchEvent(new CustomEvent('auto-start-playlist'));
              });
            }
          }
        }

        // Loja fechou — pausa playback
        if (msg.type === 'FECHAR_LOJA' && msg.clienteId === clienteId) {
          console.log('[LocutorBackend] 🌙 Loja fechada — pausando player');
          if (getIsIndoor()) {
            sendWebSocketMessage('PLAYER_PAUSE');
          } else {
            const audio = window.audioCoordinator?.audioElement;
            if (audio && !audio.paused) audio.pause();
          }
          window.dispatchEvent(new CustomEvent('loja-fechada', { detail: { clienteId } }));
          await fetchStatus();
        }
      } catch (err) {
        console.warn('[LocutorBackend] ⚠️ Erro no handler WebSocket:', err);
      }
    };
    addWebSocketListener(handler, 'useLocutorBackend');
    return () => removeWebSocketListener(handler);
  }, [clienteId, fetchStatus]);

  // ─── Ativar / desativar ────────────────────────────────────────────────────
  const ativarLocutorAutomatico = useCallback(async () => {
    if (estado.ativo) {
      await fetch(`${API}/api/locutor-scheduler/desativar/${clienteId}`, { method: 'POST' });
    } else {
      await fetch(`${API}/api/locutor-scheduler/ativar/${clienteId}`, { method: 'POST' });
    }
    await fetchStatus();
  }, [estado.ativo, clienteId, fetchStatus]);

  const setSemanticaLocutorAtiva = useCallback(async (ativa) => {
    setStatus(ativa ? '⏳ Reativando semântica...' : '⏳ Pausando semântica...');
    try {
      const r = await fetch(`${API}/api/locutor-scheduler/semantica/${clienteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ativa }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.ok === false) {
        throw new Error(d.mensagem || d.erro || 'Falha ao alterar semântica do locutor');
      }
      setEstado(d.estado || {});
      setStatus(ativa ? '✅ Semântica reativada' : '✅ Semântica pausada');
      await fetchStatus();
    } catch (err) {
      setStatus(`❌ ${err.message}`);
    }
  }, [clienteId, fetchStatus]);

  const setIndoorLocutorMode = useCallback(async (mode) => {
    const highlightsOnly = mode === 'HIGHLIGHTS_ONLY';
    setStatus(highlightsOnly
      ? '⏳ Pausando locuções automáticas...'
      : '⏳ Reativando locuções automáticas...');
    try {
      const r = await fetch(`${API}/api/locutor-scheduler/operational-mode/${clienteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.ok === false) {
        throw new Error(d.mensagem || d.erro || 'Falha ao alterar modo do locutor');
      }
      setEstado(d.estado || {});
      setStatus(highlightsOnly
        ? '✅ Somente destaques e eventos'
        : '✅ Locuções automáticas reativadas');
      await fetchStatus();
    } catch (err) {
      setStatus(`❌ ${err.message}`);
    }
  }, [clienteId, fetchStatus]);

  // ─── Falar agora imediato (botão manual) ──────────────────────────────────
  const falarAgoraImediato = useCallback(async (textoCustom) => {
    if (falando) return;
    if (!textoCustom?.trim()) {
      setErroTextoImediato('Digite o texto antes de enviar');
      return;
    }
    setFalando(true);
    setErroTextoImediato('');
    setStatus('⏳ Enviando aviso...');
    try {
      const r = await apiFetch(`${API}/api/locutor/falar-agora`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          texto: textoCustom.trim(),
        }),
      });
      const d = await r.json();
      if (d.ok) {
        setTextoImediato('');
        setStatus(`✅ Aviso na fila — toca em ~${d.previsao || 30}s`);
      } else {
        const msgs = {
          texto_vazio:     'Digite o texto antes de enviar',
          texto_longo:     'Texto muito longo (máx 280 caracteres)',
          fila_cheia:      `Aguarde ${d.restam || 120}s antes de enviar outro aviso`,
          loja_inativa:    'Loja inativa',
          codigo_invalido: 'Código da loja inválido',
        };
        setErroTextoImediato(msgs[d.erro] || d.erro || 'Erro ao enviar');
        setStatus('❌ Erro');
      }
    } catch (err) {
      setErroTextoImediato(err.message);
      setStatus(`❌ ${err.message}`);
    } finally {
      setFalando(false);
    }
  }, [falando, clienteId]);

  return {
    locutor: {
      ativo: estado.ativo,
      faixasDesdeUltimaLocucao: Number.isFinite(Number(estado.faixasDesdeUltimaLocucao))
        ? Number(estado.faixasDesdeUltimaLocucao)
        : 0,
      faixasPorLocucao: Number.isFinite(Number(estado.faixasPorLocucao))
        ? Number(estado.faixasPorLocucao)
        : null,
      semanticaLocutorAtiva: estado.semanticaLocutorAtiva !== false,
      semanticaLocutorAtualizadaEm: estado.semanticaLocutorAtualizadaEm || null,
      indoorLocutorMode: estado.indoorLocutorMode === 'HIGHLIGHTS_ONLY'
        ? 'HIGHLIGHTS_ONLY'
        : 'FULL_AUTOMATIC',
      indoorLocutorModeUpdatedAt: estado.indoorLocutorModeUpdatedAt || null,
    },
    falando,
    ultimaFala,
    status,
    configReady:      true,
    scheduledIn:      segundosProx,
    scheduledTime:    null,
    ativarLocutorAutomatico,
    setSemanticaLocutorAtiva,
    setIndoorLocutorMode,
    falarAgoraImediato,
    setStatus,
    textoImediato,
    setTextoImediato,
    erroTextoImediato,
  };
}
